import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import EyeIcon from "../../assets/image/eye-icon.svg";
import LeftAngle from "../../assets/image/left-angle.svg";
import RightAngle from "../../assets/image/right-angle.svg";
import Toastify from "../../utils/tostfy";
import { endpoint } from "../../utils/endpoints";
import { normalizeSearchInput, SEARCH_MAX_LENGTH } from "../../utils/searchInput";
import { formatDateTime } from "../../utils/formatDateTime";
import { tripStatusLabel } from "../../utils/tripStatus";
import PageNumberInput from "../../Components/Pagination/PageNumberInput";

const ACCOUNTANT_API =
  process.env.REACT_APP_ACCOUNTANT_API;

const TABLE_COL_COUNT = 12;

const parseBody = async (response) => {
  const text = await response.text();
  if (!text) return null;
  try {
    return JSON.parse(text);
  } catch {
    return { rawMessage: text };
  }
};

const getErrorMessage = (payload, response) => {
  if (!payload || typeof payload !== "object") {
    return response.statusText || "Something went wrong. Please try again.";
  }
  return (
    payload.errorMessage ||
    payload.message ||
    payload.error ||
    (Array.isArray(payload.errors) ? payload.errors.join(", ") : null) ||
    `Request failed (${response.status})`
  );
};

const extractTripBreakList = (payload) => {
  const data = payload?.data;
  if (Array.isArray(data)) return data;
  if (data && typeof data === "object") {
    if (Array.isArray(data.trip_break_data)) return data.trip_break_data;
    if (Array.isArray(data.list)) return data.list;
  }
  return [];
};

const mapTripBreakRow = (row) => {
  const routeId =
    typeof row.route_id === "object" && row.route_id != null
      ? row.route_id._id ?? row.route_id
      : row.route_id;

  return {
    id: row._id,
    tripMongoId: row._id != null ? String(row._id) : "",
    routeMongoId: routeId != null && routeId !== "" ? String(routeId) : "",
    tripId: row.trip_ref_id != null && row.trip_ref_id !== "" ? String(row.trip_ref_id) : "—",
    companyName: row.company_id?.name ?? "—",
    companyLocation: row.company_id?.company_location_id?.name ?? "—",
    oldMiplLocation: row.old_mipl_location_id?.name ?? "—",
    newMiplLocation: row.new_mipl_location_id?.name ?? "—",
    vehicle: row.vehicle_id?.name ?? "—",
    challanNumber:
      row.challan_number != null && String(row.challan_number).trim() !== ""
        ? String(row.challan_number)
        : "—",
    tripStartAt: formatDateTime(row.trip_start_at),
    tripEndAt: formatDateTime(row.trip_end_at),
    status: tripStatusLabel(row.status),
  };
};

const TripBreakManagement = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalRecords, setTotalRecords] = useState(0);
  const [apiTotalPages, setApiTotalPages] = useState(1);

  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  useEffect(() => {
    const timer = setTimeout(() => {
      setSearch(searchInput.trim());
      setCurrentPage(1);
    }, 400);
    return () => clearTimeout(timer);
  }, [searchInput]);

  useEffect(() => {
    let cancelled = false;

    const loadTripBreaks = async () => {
      const token = localStorage.getItem("sessionToken");
      if (!token) {
        toast.error("Session expired. Please log in again.");
        setRows([]);
        setLoading(false);
        return;
      }

      setLoading(true);
      const params = new URLSearchParams({
        search,
        page: String(currentPage),
        limit: String(itemsPerPage),
      });
      const url = `${ACCOUNTANT_API}${endpoint.listOfTripBreak}?${params.toString()}`;

      try {
        const response = await fetch(url, {
          method: "GET",
          headers: { Authorization: `Bearer ${token}` },
        });
        const payload = await parseBody(response);

        if (cancelled) return;

        if (!response.ok || (payload?.code != null && payload.code !== 200)) {
          toast.error(getErrorMessage(payload, response));
          setRows([]);
          setTotalRecords(0);
          setApiTotalPages(1);
          return;
        }

        const data = payload?.data || {};
        const list = extractTripBreakList(payload);

        setRows(list.map(mapTripBreakRow));
        setTotalRecords(typeof data.total === "number" ? data.total : list.length);
        setApiTotalPages(
          typeof data.total_pages === "number" && data.total_pages > 0
            ? data.total_pages
            : 1
        );
      } catch (err) {
        console.error(err);
        if (!cancelled) {
          toast.error(
            err?.message === "Failed to fetch"
              ? "Network error. Check your connection."
              : "Could not load trip breaks."
          );
          setRows([]);
          setTotalRecords(0);
          setApiTotalPages(1);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    loadTripBreaks();
    return () => {
      cancelled = true;
    };
  }, [currentPage, itemsPerPage, search]);

  const totalPages = apiTotalPages;
  const serialBase = (currentPage - 1) * itemsPerPage;

  return (
    <>
      <div className="tab-pane-content mt-7">
        <div className="flex gap-x-2.5 mb-4">
          <input
            type="text"
            placeholder="Search..."
            className="border px-3 py-2 rounded-lg outline-none text-sm searchIcon bg-white focus:!shadow-sm"
            value={searchInput}
            maxLength={SEARCH_MAX_LENGTH}
            onChange={(e) => setSearchInput(normalizeSearchInput(e.target.value))}
          />
        </div>

        <div className="common-table-wrapper m-0">
          <div className="table-responsive">
            <table>
              <thead>
                <tr>
                  <th>S.No.</th>
                  <th>Trip ID</th>
                  <th>Company</th>
                  <th>Company Location</th>
                  <th>Old MIPL Location</th>
                  <th>New MIPL Location</th>
                  <th>Vehicle</th>
                  <th>Challan No.</th>
                  <th>Trip Start</th>
                  <th>Trip End</th>
                  <th>Status</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={TABLE_COL_COUNT} className="text-center py-8 text-gray-500">
                      Loading trip breaks…
                    </td>
                  </tr>
                ) : rows.length === 0 ? (
                  <tr>
                    <td colSpan={TABLE_COL_COUNT} className="text-center py-8 text-gray-500">
                      No trip breaks found.
                    </td>
                  </tr>
                ) : (
                  rows.map((row, index) => (
                    <tr key={row.id}>
                      <td>{serialBase + index + 1}</td>
                      <td>{row.tripId}</td>
                      <td>{row.companyName}</td>
                      <td>{row.companyLocation}</td>
                      <td>{row.oldMiplLocation}</td>
                      <td>{row.newMiplLocation}</td>
                      <td>{row.vehicle}</td>
                      <td>{row.challanNumber}</td>
                      <td>{row.tripStartAt}</td>
                      <td>{row.tripEndAt}</td>
                      <td>{row.status}</td>
                      <td>
                        <div className="flex items-center justify-center gap-2">
                          {row.tripMongoId ? (
                            <Link
                              to={`/trip-assignment-management/view-trip-details?trip_id=${encodeURIComponent(
                                row.tripMongoId
                              )}${
                                row.routeMongoId
                                  ? `&route_id=${encodeURIComponent(row.routeMongoId)}`
                                  : ""
                              }&from=trip-break`}
                            >
                              <img src={EyeIcon} className="h-7 min-w-[22px]" alt="View" />
                            </Link>
                          ) : (
                            "—"
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        <div className="flex gap-x-4 items-center mt-4">
          <select
            className="p-2 shadow-sm outline-none"
            value={itemsPerPage}
            onChange={(e) => {
              setItemsPerPage(Number(e.target.value));
              setCurrentPage(1);
            }}
          >
            <option value={10}>10</option>
            <option value={20}>20</option>
          </select>

          <div className="text-sm text-gray-500 ml-auto">{totalRecords} records found</div>

          <div className="flex items-center gap-2 pr-8">
            <button
              type="button"
              onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
              disabled={currentPage === 1 || loading}
              className={`!bg-primary text-white w-[35px] h-[35px] rounded-full disabled:opacity-50 inline-flex justify-center items-center ${
                currentPage === 1 ? "" : "cursor-pointer"
              }`}
            >
              <img src={LeftAngle} className="invert grayscale brightness-100" alt="" />
            </button>
            <PageNumberInput
              page={currentPage}
              totalPages={totalPages}
              onPageChange={setCurrentPage}
              className="h-[35px] w-12 rounded border border-gray-300 text-center text-sm font-UbuntuMedium shadow-sm outline-none disabled:cursor-not-allowed disabled:bg-gray-100 disabled:opacity-70"
            />
            <span className="text-sm text-gray-600">of {totalPages}</span>
            <button
              type="button"
              onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
              disabled={currentPage === totalPages || loading}
              className={`!bg-primary text-white w-[35px] h-[35px] rounded-full disabled:opacity-50 inline-flex justify-center items-center ${
                currentPage === totalPages ? "" : "cursor-pointer"
              }`}
            >
              <img src={RightAngle} className="invert grayscale brightness-100" alt="" />
            </button>
          </div>
        </div>
      </div>
      <Toastify />
    </>
  );
};

export default TripBreakManagement;
